import React, { useEffect, useState } from 'react';
import { ArrowLeft2, ArrowRight2 } from 'iconsax-react';
import { TIPO, apiToViewEvento, toDateKey } from './mockData';

const MESI_ITA     = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const GIORNI_SHORT = ['LUN','MAR','MER','GIO','VEN','SAB','DOM'];

function dateKey(d) {
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function getCalendarDays(year, month) {
    const firstDay = new Date(year, month, 1);
    const lastDay  = new Date(year, month + 1, 0);
    // Monday-first offset (JS getDay: 0=Sun)
    const startOffset   = (firstDay.getDay() + 6) % 7;
    const trailingOffset = lastDay.getDay() === 0 ? 0 : 7 - lastDay.getDay();
    const days = [];
    for (let i = startOffset - 1; i >= 0; i--) {
        days.push({ date: new Date(year, month, 0 - i),   current: false });
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
        days.push({ date: new Date(year, month, d),        current: true  });
    }
    for (let d = 1; d <= trailingOffset; d++) {
        days.push({ date: new Date(year, month + 1, d),   current: false });
    }
    return days;
}

// ── Pagato icons ───────────────────────────────────────────────────
function CheckIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
            <circle cx="7" cy="7" r="6" stroke="#00A15D" strokeWidth="1.2"/>
            <path d="M4 7l2 2 4-4" stroke="#00A15D" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
}

function WarnIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
            <circle cx="7" cy="7" r="6" stroke="#FF9800" strokeWidth="1.2"/>
            <path d="M7 4.5v3" stroke="#FF9800" strokeWidth="1.4" strokeLinecap="round"/>
            <circle cx="7" cy="9.5" r="0.7" fill="#FF9800"/>
        </svg>
    );
}

// ── Compact event card ─────────────────────────────────────────────
function MeseCard({ event, onEventClick }) {
    const t = TIPO[event.type];
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 5,
            background: '#fff',
            borderLeft: `3px solid ${t.color}`,
            border: `1px solid #f0f0f0`,
            borderLeftWidth: 3,
            borderLeftColor: t.color,
            borderRadius: '0 3px 3px 0',
            padding: '3px 5px 3px 5px',
            marginBottom: 3,
            cursor: 'pointer',
            overflow: 'hidden',
            transition: 'box-shadow 0.12s',
        }}
            onClick={() => onEventClick && onEventClick(event)}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.10)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
        >
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {event.titolo}
                </div>
                <div style={{ fontSize: 10, color: '#aaa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    ○ {event.type === 'generico' ? 'Generico' : event.tipologia}
                </div>
            </div>
            {event.type !== 'generico' && (event.pagato ? <CheckIcon /> : <WarnIcon />)}
        </div>
    );
}

// ── Root ───────────────────────────────────────────────────────────
export default function VistaMese({ eventi = [], onRangeChange, onEventClick, onDayClick }) {
    const [year,  setYear]  = useState(() => new Date().getFullYear());
    const [month, setMonth] = useState(() => new Date().getMonth());

    useEffect(() => {
        // Include the padding days visible in the grid (Mon of first row → Sun of last row)
        const firstDay      = new Date(year, month, 1);
        const lastDay       = new Date(year, month + 1, 0);
        const startOffset   = (firstDay.getDay() + 6) % 7;          // days before 1st
        const trailingOffset = lastDay.getDay() === 0 ? 0 : 7 - lastDay.getDay(); // days after last
        const gridStart = new Date(year, month, 1 - startOffset);   // Mon of first week
        const gridEnd   = new Date(year, month + 1, trailingOffset); // Sun of last week (0 → last of month)
        const da = `${toDateKey(gridStart)}T00:00:00`;
        const a  = `${toDateKey(gridEnd)}T23:59:59`;
        onRangeChange && onRangeChange(da, a);
    }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

    const onNav = (dir) => {
        let m = month + dir;
        let y = year;
        if (m < 0)  { m = 11; y--; }
        if (m > 11) { m = 0;  y++; }
        setMonth(m);
        setYear(y);
    };

    const days = getCalendarDays(year, month);
    const numWeeks = days.length / 7;

    // Group events by date key
    const byDate = {};
    (eventi || []).forEach(ev => {
        const v = apiToViewEvento(ev);
        if (!byDate[v.data]) byDate[v.data] = [];
        byDate[v.data].push(v);
    });

    const todayKey = dateKey(new Date());

    const btnBase = {
        width: 30, height: 30,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid #e0e0e0', borderRadius: 4,
        background: '#fff', cursor: 'pointer',
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

            {/* ── Header ── */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 24px',
                borderBottom: '1px solid #f0f0f0',
                flexShrink: 0, flexWrap: 'wrap', gap: 8,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 17, fontWeight: 700, color: '#222' }}>
                        {MESI_ITA[month]} {year}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        {Object.entries(TIPO).map(([k, t]) => (
                            <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#555' }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.color, display: 'inline-block' }} />
                                {t.label}
                            </span>
                        ))}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => onNav(-1)} style={btnBase}><ArrowLeft2  size={14} color="#555" /></button>
                    <button onClick={() => onNav(1)}  style={btnBase}><ArrowRight2 size={14} color="#555" /></button>
                </div>
            </div>

            {/* ── Weekday headers ── */}
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                borderBottom: '1px solid #f0f0f0',
                flexShrink: 0,
            }}>
                {GIORNI_SHORT.map((g, i) => (
                    <div key={g} style={{
                        textAlign: 'center',
                        padding: '8px 0',
                        fontSize: 11, fontWeight: 700, color: '#bbb', letterSpacing: 0.5,
                        borderRight: i < 6 ? '1px solid #f4f4f4' : 'none',
                    }}>
                        {g}
                    </div>
                ))}
            </div>

            {/* ── Calendar grid ── */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gridTemplateRows: `repeat(${numWeeks}, 1fr)`,
                    height: '100%',
                }}>
                    {days.map(({ date, current }, i) => {
                        const key    = dateKey(date);
                        const events = byDate[key] || [];
                        const isToday   = key === todayKey;
                        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                        const colIdx    = i % 7;

                        return (
                            <div key={i} style={{
                                padding: '7px 7px 5px',
                                borderRight: colIdx < 6 ? '1px solid #f4f4f4' : 'none',
                                borderBottom: '1px solid #f4f4f4',
                                background: isWeekend && current ? '#fafafa' : '#fff',
                                overflow: 'hidden',
                                minHeight: 0,
                            }}>
                                {/* Day number */}
                                <div style={{ marginBottom: 5 }}>
                                    <span
                                        onClick={() => onDayClick && onDayClick(new Date(date))}
                                        title="Vai al giorno"
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            width: 24, height: 24, borderRadius: '50%',
                                            fontSize: 13, fontWeight: 700, lineHeight: 1,
                                            background: isToday ? '#FF5E4B' : 'transparent',
                                            color: isToday ? '#fff' : current ? '#222' : '#ccc',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {date.getDate()}
                                    </span>
                                </div>
                                {/* Events */}
                                {events.map(ev => <MeseCard key={ev.id} event={ev} onEventClick={onEventClick} />)}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
